/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

/**
 * Converts a base64 encoded string to a SHA1 hash string
 * @param base64String - The base64 encoded string to convert
 * @returns A promise that resolves to the SHA1 hash as a hexadecimal string, or empty string if input is undefined/null/empty or if conversion fails
 */
export const base64ToSha1 = async (
  base64String?: string | null
): Promise<string> => {
  // Handle edge cases: undefined, null, or empty string
  if (!base64String || base64String.trim() === '') {
    return '';
  }

  try {
    // Decode base64 string to binary data
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Compute SHA1 hash using Web Crypto API
    const hashBuffer = await crypto.subtle.digest('SHA-1', bytes);

    // Convert hash to hexadecimal string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');

    return hashHex;
  } catch (error) {
    console.error(
      `Failed to convert base64 to SHA1: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
    return '';
  }
};

